import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { plainConfirmMessage, usePreviewConfirm } from "@/lib/confirmFlow";
import { formatCents } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Facts, Problem, ServerSays } from "@/components/PreviewBits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useContactNames } from "@/features/billing/useContactNames";
import { useAllAffiliates, type Affiliate } from "./affiliates";

/** affiliates_credit_order's answer (the preview has the same shape, before the commissions are written). */
export interface CreditOrderOut {
  order_id: number;
  affiliate_id: number;
  code: string;
  payments: number;
  collected_cents: number;
  level1_percent: number;
  level2_percent: number;
  recruiter?: string;
  commissions_cents: number;
  notes?: string[];
}

/** Active partners, matched on code or name (case-insensitive), sorted by code. */
export function matchPartners(all: Affiliate[], names: (id: number) => string, query: string): Affiliate[] {
  const q = query.trim().toLowerCase();
  return all
    .filter((a) => a.status === "active")
    .filter((a) => !q || a.code.toLowerCase().includes(q) || names(a.contact_id).toLowerCase().includes(q))
    .sort((a, b) => a.code.localeCompare(b.code));
}

/** The order page's partner line: who the sale is credited to, or "Credit to a partner" when nobody is. */
export function OrderPartner({ orderId, affiliateId, keapOrder, buyerId }: { orderId: number; affiliateId: number | null; keapOrder: boolean; buyerId: number }) {
  const all = useAllAffiliates();
  const [open, setOpen] = useState(false);
  if (affiliateId) {
    const a = all.data?.find((x) => x.id === affiliateId);
    return (
      <span className="self-center text-sm">
        <span className="text-[var(--color-muted-foreground)]">Partner credited: </span>
        <Link to="/affiliates/$affiliateId" params={{ affiliateId: String(affiliateId) }} className="font-medium text-[var(--color-primary)] hover:underline">
          {a ? a.code : `#${affiliateId}`}
        </Link>
      </span>
    );
  }
  if (keapOrder) return null; // Keap pays the commissions on its own orders
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Credit to a partner
      </Button>
      {open && <CreditOrderDialog orderId={orderId} buyerId={buyerId} onClose={() => setOpen(false)} />}
    </>
  );
}

/** affiliates_credit_order: choose the partner, the server's preview (what they'll earn), then confirm. */
export function CreditOrderDialog({ orderId, buyerId, onClose }: { orderId: number; buyerId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const all = useAllAffiliates();
  const active = useMemo(() => (all.data ?? []).filter((a) => a.status === "active" && a.contact_id !== buyerId), [all.data, buyerId]);
  const name = useContactNames(active.map((a) => a.contact_id));
  const [query, setQuery] = useState("");
  const [pick, setPick] = useState<number | "">("");
  const [note, setNote] = useState("");
  const shown = matchPartners(active, name, query).slice(0, 200);
  const chosen = active.find((a) => a.id === pick);
  const flow = usePreviewConfirm<CreditOrderOut, CreditOrderOut>(
    (confirm) =>
      api<CreditOrderOut>("/api/v1/affiliate-commissions/credit-order", {
        json: { order_id: orderId, affiliate_id: pick || undefined, note: note.trim() || undefined, confirm: confirm || undefined },
      }),
    (r) => {
      for (const k of ["order", "orders", "affiliates", "affiliate", "affiliate-commissions", "payouts-due"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(`Order #${orderId} credited to ${r.code}${r.commissions_cents ? `: ${formatCents(r.commissions_cents)} in commissions` : ""}`);
      onClose();
    },
  );
  const p = flow.preview?.details;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))]">
        <DialogTitle>{p ? "Check before crediting" : `Credit order #${orderId} to a partner`}</DialogTitle>
        {!p ? (
          <>
            <DialogDescription>
              For a sale the CRM couldn't link to a partner: a printed Keap partner link, a phone sale, or a partner who says they sent the buyer. The partner earns commissions on
              what's been paid and on later payments. Nothing changes until you confirm on the next step.
            </DialogDescription>
            <div className="space-y-3 text-sm">
              <div className="space-y-1">
                <Label htmlFor="co-find">Find a partner (code or name)</Label>
                <Input id="co-find" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="woodyg017" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="co-pick">Partner</Label>
                <Select id="co-pick" value={pick} onChange={(e) => setPick(e.target.value ? Number(e.target.value) : "")}>
                  <option value="">{all.isLoading ? "Loading partners…" : `Choose (${shown.length} active)…`}</option>
                  {shown.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code}: {name(a.contact_id)}
                    </option>
                  ))}
                </Select>
                {all.error && <p className="text-xs text-[var(--color-danger-fg)]">Couldn't load partners: {all.error.message}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="co-note">Why (kept on the timeline)</Label>
                <Input id="co-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Came from Woody's postcard (printed Keap link)" />
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <ServerSays text={plainConfirmMessage(flow.preview!.message)} />
            <Facts
              rows={[
                ["Partner", chosen ? `${p.code} (${name(chosen.contact_id)})` : p.code],
                ["Paid so far", `${formatCents(p.collected_cents)} in ${p.payments} payment${p.payments === 1 ? "" : "s"}`],
                ["Their rate", `${p.level1_percent}%`],
                ["Recruiter (second level)", p.recruiter ? `${p.recruiter} at ${p.level2_percent}%` : "none"],
              ]}
            />
            {p.notes && p.notes.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-xs text-[var(--color-muted-foreground)]">
                {p.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <Problem text={flow.problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={p ? flow.back : onClose}>
            {p ? "Back" : "Cancel"}
          </Button>
          {!p ? (
            <Button disabled={!pick || flow.reviewing} onClick={flow.review}>
              Review
            </Button>
          ) : (
            <Button disabled={flow.confirming} onClick={flow.confirm}>
              Credit to {p.code}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
